<script setup lang="ts">
import { ref, computed } from 'vue'

interface DepositAmount {
  value: number
  bonus: number // Percentage
}

const amounts: DepositAmount[] = [
  { value: 100, bonus: 0 },
  { value: 200, bonus: 5 },
  { value: 500, bonus: 5 },
  { value: 1000, bonus: 5 },
  { value: 2000, bonus: 8 },
  { value: 3000, bonus: 8 },
  { value: 5000, bonus: 8 },
  { value: 10000, bonus: 10 },
  { value: 20000, bonus: 10 },
]

const selectedAmount = ref<DepositAmount>(amounts[3]!)

const bonusAmount = computed(() => {
  if (!selectedAmount.value) return 0
  return Math.floor((selectedAmount.value.value * selectedAmount.value.bonus) / 100)
})

const totalAmount = computed(() => {
  if (!selectedAmount.value) return 0
  return selectedAmount.value.value + bonusAmount.value
})

const formatNum = (n: number) => n.toLocaleString('en-IN')
</script>

<template>
  <div class="deposit-panel">

    <!-- Ticker Bar -->
    <div class="panel-ticker">
      <span class="ticker-icon">📢</span>
      <div class="ticker-scroll">
        <div class="ticker-inner">
          <img src="@/assets/hall/usericons/1.webp" class="user-head" />
          <span class="ticker-text">Guest**05 <span class="ticker-hl">Won Rs 4,723.37</span> 🎉 &nbsp;&nbsp;&nbsp; Guest**88 just deposited Rs 5,000 💰</span>
        </div>
      </div>
    </div>

    <!-- Main Two-Column Layout -->
    <div class="deposit-body">

      <!-- LEFT: Form -->
      <div class="col-form">

        <!-- Amount Title -->
        <div class="section-header">
          <div class="section-icon">💳</div>
          <div>
            <div class="section-title">Select Amount</div>
            <div class="section-sub">Choose your deposit amount below</div>
          </div>
        </div>

        <!-- Amount Grid -->
        <div class="amount-grid">
          <div
            v-for="item in amounts"
            :key="item.value"
            class="amount-item"
            :class="{ active: selectedAmount.value === item.value, 'has-bonus': item.bonus > 0 }"
            @click="selectedAmount = item"
          >
            <div class="amount-value">Rs {{ formatNum(item.value) }}</div>
            <div v-if="item.bonus > 0" class="bonus-tag">+{{ item.bonus }}% Bonus</div>
            <div v-if="selectedAmount.value === item.value" class="check-mark">✓</div>
          </div>
        </div>

        <!-- Summary Card -->
        <div class="summary-card">
          <div class="summary-title">Order Summary</div>

          <div class="summary-rows">
            <div class="summary-row">
              <span class="row-label">
                <span class="row-dot dot-blue"></span>Deposit Amount
              </span>
              <span class="row-val">Rs {{ formatNum(selectedAmount.value) }}</span>
            </div>
            <div class="summary-row">
              <span class="row-label">
                <span class="row-dot dot-green"></span>Bonus
              </span>
              <span class="row-val bonus-val">+ Rs {{ formatNum(bonusAmount) }}</span>
            </div>
            <div class="summary-row">
              <span class="row-label">
                <span class="row-dot dot-purple"></span>Diamonds
              </span>
              <span class="row-val">Rs 0</span>
            </div>
          </div>

          <div class="summary-divider"></div>

          <div class="summary-total">
            <span class="total-label">Total Get</span>
            <span class="total-val">Rs {{ formatNum(totalAmount) }}</span>
          </div>
        </div>

        <!-- Pay Button -->
        <button class="pay-btn" @click="console.log('Paying Rs', selectedAmount.value)">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
          Pay Now — Rs {{ formatNum(totalAmount) }}
        </button>

        <!-- Instructions -->
        <div class="instructions">
          <div class="inst-item">
            <span class="inst-icon">⏱</span>
            Each deposit will be credited within 15 minutes.
          </div>
          <div class="inst-item">
            <span class="inst-icon">🔒</span>
            Every deposit order will be randomly increased by 1–10 Rs to ensure safety.
          </div>
        </div>
      </div>

      <!-- RIGHT: Info Sidebar -->
      <aside class="col-info">

        <!-- Bonus Tiers Card -->
        <div class="info-card bonus-tiers-card">
          <div class="info-card-title">
            <span>🎁</span> Deposit Bonus Tiers
          </div>
          <div class="tier-list">
            <div class="tier-row" v-for="(tier, i) in [
              { label: 'Rs 200 – Rs 500', bonus: '5%', color: '#4ade80' },
              { label: 'Rs 1,000 – Rs 5,000', bonus: '8%', color: '#facc15' },
              { label: 'Rs 10,000+', bonus: '10%', color: '#f97316' },
            ]" :key="i">
              <span class="tier-label">{{ tier.label }}</span>
              <span class="tier-bonus" :style="{ color: tier.color }">{{ tier.bonus }}</span>
            </div>
          </div>
        </div>

        <!-- Fast Deposit Card -->
        <div class="info-card fast-card">
          <div class="info-card-title">
            <span>⚡</span> Fast & Secure
          </div>
          <div class="fast-features">
            <div class="feat-item">
              <div class="feat-icon">🚀</div>
              <div class="feat-text">
                <div class="feat-name">Instant Credit</div>
                <div class="feat-desc">Usually within 2 minutes</div>
              </div>
            </div>
            <div class="feat-item">
              <div class="feat-icon">🔐</div>
              <div class="feat-text">
                <div class="feat-name">SSL Encrypted</div>
                <div class="feat-desc">256-bit secure connection</div>
              </div>
            </div>
            <div class="feat-item">
              <div class="feat-icon">🏦</div>
              <div class="feat-text">
                <div class="feat-name">Multiple Methods</div>
                <div class="feat-desc">UPI, Bank Transfer & more</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Support Banner -->
        <div class="info-card support-card">
          <div class="support-icon">🎧</div>
          <div class="support-text">
            <div class="support-title">Need Help?</div>
            <div class="support-sub">Our support team is available 24/7</div>
          </div>
          <button class="support-btn">Chat</button>
        </div>

      </aside>
    </div>
  </div>
</template>

<style scoped>
/* ===========================
   Panel Shell
   =========================== */
.deposit-panel {
  width: 100%;
  min-height: calc(100vh - 60px);
  background: var(--theme-nav-bg, #1a1a1a);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #fff;
  display: flex;
  flex-direction: column;
}

/* ===========================
   Ticker
   =========================== */
.panel-ticker {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  background: var(--theme-lobby-bg, rgba(0,0,0,0.5));
  border-bottom: 1px solid rgba(255,255,255,0.06);
  overflow: hidden;
}

.ticker-icon { font-size: 1rem; flex-shrink: 0; }

.ticker-scroll {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.ticker-inner {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  animation: tickerScroll 18s linear infinite;
}

@keyframes tickerScroll {
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
}

.user-head {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ticker-text { font-size: 0.82rem; color: #bbb; }
.ticker-hl { color: var(--theme-accent, #ffda6b); font-weight: 700; }

/* ===========================
   Body Layout
   =========================== */
.deposit-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
}

/* ===========================
   Section Header
   =========================== */
.section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.section-icon {
  width: 44px;
  height: 44px;
  background: var(--theme-bg-card, rgba(34,51,34,0.45));
  border: 1px solid var(--theme-border-card, #4ca32e);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
}

.section-sub {
  font-size: 0.8rem;
  color: #777;
  margin-top: 2px;
}

/* ===========================
   Amount Grid
   =========================== */
.amount-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 22px;
}

.amount-item {
  position: relative;
  height: 64px;
  background: rgba(255, 255, 255, 0.05);
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.amount-item:hover {
  background: rgba(255, 255, 255, 0.09);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.amount-item.active {
  background: var(--theme-primary, #4ca32e);
  border-color: var(--theme-accent, #ffda6b);
  box-shadow: 0 0 0 1px var(--theme-accent, #ffda6b), 0 6px 20px rgba(76,163,46,0.35);
  transform: translateY(-2px);
}

.amount-value {
  font-weight: 700;
  font-size: 1.05rem;
  color: #ccc;
}

.amount-item.active .amount-value {
  color: #fff;
  font-size: 1.1rem;
}

.bonus-tag {
  font-size: 0.65rem;
  font-weight: 700;
  color: #ff9800;
  background: rgba(255, 152, 0, 0.15);
  border-radius: 4px;
  padding: 1px 5px;
}

.amount-item.active .bonus-tag {
  color: #ffda6b;
  background: rgba(255, 218, 107, 0.2);
}

.check-mark {
  position: absolute;
  top: 6px;
  right: 9px;
  font-size: 0.75rem;
  color: var(--theme-accent, #ffda6b);
  font-weight: 700;
}

/* ===========================
   Summary Card
   =========================== */
.summary-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 18px 20px;
  margin-bottom: 20px;
}

.summary-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #666;
  margin-bottom: 14px;
  font-weight: 600;
}

.summary-rows { display: flex; flex-direction: column; gap: 10px; }

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.88rem;
}

.row-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #888;
}

.row-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-blue { background: #60a5fa; box-shadow: 0 0 6px rgba(96,165,250,0.5); }
.dot-green { background: #4ade80; box-shadow: 0 0 6px rgba(74,222,128,0.5); }
.dot-purple { background: #a78bfa; box-shadow: 0 0 6px rgba(167,139,250,0.5); }

.row-val { color: #fff; font-weight: 600; }
.bonus-val { color: #4ade80; }

.summary-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 14px 0;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 0.9rem;
  color: #aaa;
  font-weight: 600;
}

.total-val {
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--theme-text-highlight, #ffda6b);
  letter-spacing: -0.5px;
}

/* ===========================
   Pay Button
   =========================== */
.pay-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 54px;
  background: var(--theme-btn-gradient, linear-gradient(to bottom, #f9e19d 0%, #d89f3c 50%, #b37e2a 100%));
  color: #4a2e00;
  border: none;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 900;
  margin-bottom: 18px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4), inset 0 2px 5px rgba(255,255,255,0.35);
  transition: transform 0.15s, box-shadow 0.15s;
  letter-spacing: 0.2px;
}

.pay-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5), inset 0 2px 5px rgba(255,255,255,0.35);
}

.pay-btn:active {
  transform: scale(0.98);
  box-shadow: 0 3px 10px rgba(0,0,0,0.4);
}

/* ===========================
   Instructions
   =========================== */
.instructions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.inst-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.78rem;
  color: #555;
  line-height: 1.5;
}

.inst-icon { flex-shrink: 0; font-size: 0.9rem; }

/* ===========================
   Info Sidebar Cards
   =========================== */
.info-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 18px 20px;
}

.info-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #ccc;
  margin-bottom: 14px;
}

/* Bonus Tiers */
.tier-list { display: flex; flex-direction: column; gap: 10px; }

.tier-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  padding: 8px 12px;
  background: rgba(255,255,255,0.04);
  border-radius: 8px;
}

.tier-label { color: #aaa; }
.tier-bonus { font-weight: 800; font-size: 1rem; }

/* Fast Features */
.fast-features { display: flex; flex-direction: column; gap: 12px; }

.feat-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.feat-icon {
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.06);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.feat-name { font-size: 0.85rem; font-weight: 600; color: #ddd; }
.feat-desc { font-size: 0.75rem; color: #666; margin-top: 1px; }

/* Support Card */
.support-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, rgba(76,163,46,0.12), rgba(76,163,46,0.05));
  border-color: rgba(76,163,46,0.25);
}

.support-icon { font-size: 1.6rem; flex-shrink: 0; }
.support-text { flex: 1 }
.support-title { font-size: 0.9rem; font-weight: 700; color: #ddd; }
.support-sub { font-size: 0.75rem; color: #777; margin-top: 2px; }

.support-btn {
  background: var(--theme-primary, #4ca32e);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 8px 16px;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.2s;
}
.support-btn:hover { opacity: 0.85; }

/* ===========================
   PC Layout (>= 900px)
   =========================== */
@media (min-width: 900px) {
  .deposit-body {
    flex-direction: row;
    align-items: flex-start;
    gap: 28px;
    padding: 28px 36px;
    max-width: 1100px;
    margin: 0 auto;
    width: 100%;
  }

  .col-form {
    flex: 1;
    min-width: 0;
  }

  .col-info {
    flex: 0 0 300px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 80px;
  }

  .amount-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .amount-item {
    height: 72px;
  }
}

@media (min-width: 1200px) {
  .deposit-body {
    max-width: 1200px;
    padding: 32px 48px;
  }

  .col-info {
    flex: 0 0 340px;
  }
}

/* Mobile: hide sidebar */
@media (max-width: 899px) {
  .col-info {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
}
</style>
